import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flag, Trash2, Eye, FileText, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BulkActions } from './BulkActions';
import { SplitPane } from './SplitPane';
import { ResponsiveImage } from '@/components/ResponsiveImage';

interface BorderReport {
  id: string;
  user_name: string;
  crossing: string;
  timestamp: string;
  type: 'car' | 'pedestrian';
  image_url?: string;
  status: 'active' | 'flagged' | 'removed';
  wait_time?: number;
  notes?: string;
}

interface BorderReportsProps {
  reports: BorderReport[];
  searchTerm: string;
  typeFilter: string;
  statusFilter: string;
  onSearchChange: (value: string) => void;
  onTypeFilterChange: (value: string) => void;
  onStatusFilterChange: (value: string) => void;
  onFlagReport: (reportId: string) => void;
  onRemoveReport: (reportId: string) => void;
  onViewReport: (reportId: string) => void;
  isSpanish: boolean;
}

type SortField = 'user_name' | 'crossing' | 'timestamp' | 'type' | 'status';
type SortDirection = 'asc' | 'desc';

const BorderReports: React.FC<BorderReportsProps> = ({
  reports,
  searchTerm,
  typeFilter,
  statusFilter,
  onSearchChange,
  onTypeFilterChange,
  onStatusFilterChange,
  onFlagReport,
  onRemoveReport,
  onViewReport,
  isSpanish
}) => {
  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedReport, setSelectedReport] = useState<BorderReport | null>(null);
  const [showSplitView, setShowSplitView] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'default',
      flagged: 'destructive',
      removed: 'secondary'
    } as const;

    const labels = {
      active: isSpanish ? 'Activo' : 'Active',
      flagged: isSpanish ? 'Marcado' : 'Flagged',
      removed: isSpanish ? 'Removido' : 'Removed'
    };

    return (
      <Badge variant={variants[status as keyof typeof variants]}>
        {labels[status as keyof typeof labels]}
      </Badge>
    );
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedReports = useMemo(() => {
    let filtered = reports.filter(report => {
      const matchesSearch = report.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           report.crossing.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = typeFilter === 'all' || report.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
      return matchesSearch && matchesType && matchesStatus;
    });

    return filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      return (aVal > bVal ? 1 : -1) * direction;
    });
  }, [reports, searchTerm, typeFilter, statusFilter, sortField, sortDirection]);

  const handleBulkFlag = () => {
    selectedReports.forEach(id => onFlagReport(id));
    setSelectedReports(new Set());
  };

  const handleBulkRemove = () => {
    if (confirm(isSpanish ? '¿Eliminar reportes seleccionados?' : 'Remove selected reports?')) {
      selectedReports.forEach(id => onRemoveReport(id));
      setSelectedReports(new Set());
    }
  };

  const handleBulkExport = () => {
    const selectedData = reports.filter(r => selectedReports.has(r.id));
    const csv = [
      ['User', 'Crossing', 'Timestamp', 'Type', 'Status', 'Wait Time'],
      ...selectedData.map(r => [
        r.user_name,
        r.crossing,
        r.timestamp,
        r.type,
        r.status,
        r.wait_time || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `border-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleRowClick = (report: BorderReport) => {
    setSelectedReport(report);
    if (showSplitView) {
      // Already in split view, just update selection
    } else {
      setShowDetailDialog(true);
    }
  };

  const bulkActions = [
    {
      id: 'flag',
      label: isSpanish ? 'Marcar' : 'Flag',
      icon: Flag,
      action: handleBulkFlag
    },
    {
      id: 'remove',
      label: isSpanish ? 'Eliminar' : 'Remove',
      icon: Trash2,
      variant: 'destructive' as const,
      action: handleBulkRemove
    },
    {
      id: 'export',
      label: isSpanish ? 'Exportar' : 'Export',
      icon: Download,
      action: handleBulkExport
    }
  ];

  const tableColumns = [
    {
      id: 'user_name',
      label: isSpanish ? 'Usuario' : 'User',
      accessor: 'user_name' as keyof BorderReport,
      sortable: true
    },
    {
      id: 'crossing',
      label: isSpanish ? 'Cruce' : 'Crossing',
      accessor: 'crossing' as keyof BorderReport,
      sortable: true
    },
    {
      id: 'timestamp',
      label: isSpanish ? 'Fecha' : 'Timestamp',
      accessor: (row: BorderReport) => new Date(row.timestamp).toLocaleString(),
      sortable: true
    },
    {
      id: 'type',
      label: isSpanish ? 'Tipo' : 'Type',
      accessor: (row: BorderReport) => (
        <Badge variant="outline">
          {row.type === 'car' ? (isSpanish ? 'Automóvil' : 'Car') : (isSpanish ? 'Peatón' : 'Pedestrian')}
        </Badge>
      ),
      sortable: true
    },
    {
      id: 'image_url',
      label: isSpanish ? 'Imagen' : 'Image',
      accessor: (row: BorderReport) => row.image_url ? (
        <ResponsiveImage 
          src={row.image_url} 
          alt="Report" 
          width={48}
          height={48}
          className="w-12 h-12 rounded"
          sizes="48px"
        />
      ) : null
    },
    {
      id: 'status',
      label: isSpanish ? 'Estado' : 'Status',
      accessor: (row: BorderReport) => getStatusBadge(row.status),
      sortable: true
    },
    {
      id: 'actions',
      label: isSpanish ? 'Acciones' : 'Actions',
      accessor: (report: BorderReport) => (
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(report);
            }}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onFlagReport(report.id);
            }}
            disabled={report.status === 'flagged'}
          >
            <Flag className="w-4 h-4" />
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRemoveReport(report.id);
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const ReportDetailView = ({ report }: { report: BorderReport }) => (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-4">
          {isSpanish ? 'Detalles del Reporte' : 'Report Details'}
        </h3>
      </div>

      {report.image_url && (
        <div>
          <h4 className="text-sm font-medium mb-2">{isSpanish ? 'Imagen' : 'Image'}</h4>
          <img
            src={report.image_url}
            alt="Report"
            className="w-full max-w-md rounded-lg border"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-muted-foreground">{isSpanish ? 'Usuario' : 'User'}</p>
          <p className="font-medium">{report.user_name}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{isSpanish ? 'Cruce' : 'Crossing'}</p>
          <p className="font-medium">{report.crossing}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{isSpanish ? 'Fecha' : 'Timestamp'}</p>
          <p className="font-medium">{new Date(report.timestamp).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{isSpanish ? 'Tipo' : 'Type'}</p>
          <p className="font-medium">
            {report.type === 'car' ? (isSpanish ? 'Automóvil' : 'Car') : (isSpanish ? 'Peatón' : 'Pedestrian')}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{isSpanish ? 'Estado' : 'Status'}</p>
          {getStatusBadge(report.status)}
        </div>
        {report.wait_time && (
          <div>
            <p className="text-sm text-muted-foreground">{isSpanish ? 'Tiempo de Espera' : 'Wait Time'}</p>
            <p className="font-medium">{report.wait_time} min</p>
          </div>
        )}
      </div>

      {report.notes && (
        <div>
          <p className="text-sm text-muted-foreground mb-2">{isSpanish ? 'Notas' : 'Notes'}</p>
          <p className="text-sm p-3 bg-muted rounded-lg">{report.notes}</p>
        </div>
      )}

      <div className="flex gap-2 pt-4 border-t">
        <Button
          onClick={() => onFlagReport(report.id)}
          disabled={report.status === 'flagged'}
          variant="outline"
          className="flex-1"
        >
          <Flag className="w-4 h-4 mr-2" />
          {isSpanish ? 'Marcar Reporte' : 'Flag Report'}
        </Button>
        <Button
          onClick={() => onRemoveReport(report.id)}
          variant="destructive"
          className="flex-1"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {isSpanish ? 'Eliminar' : 'Remove'}
        </Button>
      </div>
    </div>
  );

  const mainContent = (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>
              {isSpanish ? 'Reportes de Frontera' : 'Border Reports'}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSplitView(!showSplitView)}
            >
              <FileText className="w-4 h-4 mr-2" />
              {showSplitView 
                ? (isSpanish ? 'Vista Simple' : 'Simple View')
                : (isSpanish ? 'Vista Dividida' : 'Split View')
              }
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <Input
              placeholder={isSpanish ? 'Buscar por usuario o cruce...' : 'Search by user or crossing...'}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="flex-1"
            />
            <Select value={typeFilter} onValueChange={onTypeFilterChange}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder={isSpanish ? 'Tipo' : 'Type'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
                <SelectItem value="car">{isSpanish ? 'Automóvil' : 'Car'}</SelectItem>
                <SelectItem value="pedestrian">{isSpanish ? 'Peatón' : 'Pedestrian'}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder={isSpanish ? 'Estado' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
                <SelectItem value="active">{isSpanish ? 'Activo' : 'Active'}</SelectItem>
                <SelectItem value="flagged">{isSpanish ? 'Marcado' : 'Flagged'}</SelectItem>
                <SelectItem value="removed">{isSpanish ? 'Removido' : 'Removed'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedReports.size > 0 && (
            <BulkActions
              selectedCount={selectedReports.size}
              actions={bulkActions}
              onClearSelection={() => setSelectedReports(new Set())}
              isSpanish={isSpanish}
            />
          )}

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {tableColumns.map(col => (
                    <TableHead key={col.id}>{col.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedReports.map(report => (
                  <TableRow key={report.id} onClick={() => handleRowClick(report)} className="cursor-pointer">
                    {tableColumns.map(col => (
                      <TableCell key={col.id}>
                        {typeof col.accessor === 'function' ? col.accessor(report) : report[col.accessor]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (showSplitView && selectedReport) {
    return (
      <SplitPane
        left={mainContent}
        right={<ReportDetailView report={selectedReport} />}
        defaultSize={60}
      />
    );
  }

  return (
    <>
      {mainContent}
      
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {isSpanish ? 'Detalles del Reporte' : 'Report Details'}
            </DialogTitle>
          </DialogHeader>
          {selectedReport && <ReportDetailView report={selectedReport} />}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BorderReports;
